/** Filtres de liste — mêmes statuts que les pastilles, plus les vues du tableau de bord. */
import { docStatusKind, type DocStatusKind } from "../../status/docStatus";
import { quoteValidUntil, type DocKind, type Quote } from "./types";
import type { ListFilter } from "./QuotesListBar";

/** Jour local, midi, pour éviter un décalage de date autour de minuit. */
export function localDay(offset = 0): string {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function invoiceRemain(row: Quote): number {
  if (row.remaining_cents != null) {
    const n = Number(row.remaining_cents);
    if (!Number.isNaN(n)) return n;
  }
  if (row.paid) return 0;
  return Math.max(0, (Number(row.total_ttc_cents) || 0) - (Number(row.paid_cents) || 0));
}

/** Même découpage que le tableau de bord : validée, non annulée, reste à payer, échéance passée ou non. */
function invoiceBucket(row: Quote): "overdue" | "upcoming" | "" {
  if ((row.doc_type || "invoice") !== "invoice") return "";
  if (row.status !== "validated" || row.credited) return "";
  const due = (row.due_date || "").slice(0, 10);
  if (!due || invoiceRemain(row) <= 0) return "";
  return due < localDay() ? "overdue" : "upcoming";
}

/** Devis validé, non converti. `horizon` 0 = déjà expiré, 14 = expiré ou dans les 14 jours. */
function quoteInHorizon(row: Quote, horizon: number): boolean {
  if ((row.doc_type || "quote") !== "quote") return false;
  if (row.status !== "validated" || row.converted_invoice_id || row.shelved_at) return false;
  const until = quoteValidUntil(row).slice(0, 10);
  if (!until) return false;
  if (horizon <= 0) return until < localDay();
  return until <= localDay(horizon);
}

export function docFilterChips(kind: DocKind): ListFilter[] {
  if (kind === "invoice") {
    return [
      { id: "all", label: "Toutes" },
      { id: "draft", label: "Brouillon" },
      { id: "sent", label: "Envoyé" },
      { id: "viewed", label: "Vu" },
      { id: "partial", label: "Partiel" },
      { id: "overdue", label: "En retard" },
      { id: "upcoming", label: "À encaisser" },
      { id: "paid", label: "Payé" },
      { id: "credited", label: "Annulée" },
      { id: "collecting", label: "En paiement" },
      { id: "rejected", label: "Rejeté" },
    ];
  }
  if (kind === "credit_note") {
    return [
      { id: "all", label: "Tous" },
      { id: "draft", label: "Brouillon" },
      { id: "sent", label: "Envoyé" },
      { id: "viewed", label: "Vu" },
    ];
  }
  return [
    { id: "all", label: "Tous" },
    { id: "draft", label: "Brouillon" },
    { id: "sent", label: "Envoyé" },
    { id: "viewed", label: "Vu" },
    { id: "signed", label: "Signé" },
    { id: "open", label: "Non converti" },
    { id: "relance", label: "À relancer" },
    { id: "expired", label: "Expiré" },
    { id: "converted", label: "Converti" },
  ];
}

export function docMatchesFilter(row: Quote, kind: DocKind, filter: string): boolean {
  if (!filter || filter === "all") return true;
  if (kind === "invoice" && (filter === "overdue" || filter === "upcoming")) return invoiceBucket(row) === filter;
  if (kind === "quote" && filter === "relance") return quoteInHorizon(row, 14);
  if (kind === "quote" && filter === "expired") return quoteInHorizon(row, 0);
  if (kind === "quote" && filter === "open") return docStatusKind(row) !== "converted";
  return docStatusKind(row) === (filter as DocStatusKind);
}

export const REC_FILTERS: ListFilter[] = [
  { id: "all", label: "Toutes" },
  { id: "draft", label: "Brouillon" },
  { id: "waiting", label: "En attente" },
  { id: "running", label: "Active" },
  { id: "soon", label: "À venir" },
  { id: "paused", label: "En pause" },
  { id: "ended", label: "Terminée" },
];
